import { Button, Col, Input, Row, Modal, Spin, message } from "antd";
import FormLayout, { FormSection, FormItem } from "Components/form";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import Yup from "ValidationModels/localizedYup";
import MeasureList from "Components/form/measure-list";
import { UnitStoreModel, ACTION_TYPES } from "store/models/master-data/unit";
import { createComponentStore } from "easy-peasy";
import { yupResolver } from "@hookform/resolvers";
import { DISPLAY_MODE } from "Constants";

const useUnitsStore = createComponentStore(UnitStoreModel);

const FormValidationSchema = Yup.object().shape({
  description: Yup.string().max(1000).required(),
  district: Yup.string().required(),
  province: Yup.string().required(),
  address: Yup.string().required(),
});

export default function UnitForm({
  unit,
  onAfterSuccess,
  loading = false,
  mode = DISPLAY_MODE.READ_ONLY,
  ...props
}) {
  const [store, actions] = useUnitsStore();

  const form = useForm({
    mode: "onChange",
    resolver: yupResolver(FormValidationSchema),
    defaultValues: unit,
  });

  const { control, handleSubmit, errors, watch, reset, formState } = form;

  useEffect(() => {
    if (unit) reset(unit);
  }, [unit, reset]);

  useEffect(() => {
    if (store.response)
      switch (store.process) {
        case ACTION_TYPES.CREATED_DATA:
          message.success("Birim oluşturuldu.");
          if (onAfterSuccess) onAfterSuccess(store.response);
          break;
        case ACTION_TYPES.UPDATED_DATA:
          message.success("Birim güncellendi.");
          if (onAfterSuccess) onAfterSuccess(store.response);
          break;
        default:
          break;
      }
  }, [store.process, store.response]);

  const formOnSubmit = async (form) => {
    if (mode === DISPLAY_MODE.EDIT) {
      let payload = {
        ...unit,
        ...form,
        measures: form.measures?.map((measure) => {
          if (measure.id)
            return {
              ...unit.measures?.find((f) => f.id === measure.id),
              ...measure,
            };

          return measure;
        }),
      };

      actions.update(payload);
    }

    if (mode === DISPLAY_MODE.NEW) actions.create(form);
  };

  return (
    <Spin spinning={loading}>
      <form onSubmit={handleSubmit(formOnSubmit)} style={{ margin: "0 24px" }}>
        <FormLayout>
          <Col lg={24}>
            <FormSection title="Genel">
              <Row gutter={16}>
                {unit?.code ? (
                  <Col sm={24}>
                    <FormItem label="Kod" required={true}>
                      <Controller
                        control={control}
                        name="code"
                        as={<Input readOnly={true} />}
                      />
                    </FormItem>
                  </Col>
                ) : null}
                <Col sm={24}>
                  <FormItem
                    label="Açıklama"
                    error={errors.description}
                    required={true}
                  >
                    <Controller
                      control={control}
                      name="description"
                      as={
                        <Input.TextArea
                          rows={3}
                          placeholder="Açıklama yazınız"
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                        />
                      }
                    />
                  </FormItem>
                </Col>
              </Row>
            </FormSection>
            <FormSection title="Konum">
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <FormItem label="İl" error={errors.province} required={true}>
                    <Controller
                      control={control}
                      name="province"
                      as={
                        <Input
                          placeholder="İl yazınız"
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                        />
                      }
                    />
                  </FormItem>
                </Col>
                <Col xs={24} md={8}>
                  <FormItem
                    label="İlçe"
                    error={errors.district}
                    required={true}
                  >
                    <Controller
                      control={control}
                      name="district"
                      as={
                        <Input
                          placeholder="İlçe yazınız"
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                        />
                      }
                    />
                  </FormItem>
                </Col>
                <Col xs={24} md={8}>
                  <FormItem
                    label="Mahalle"
                    error={errors.neighborhood}
                    required={true}
                  >
                    <Controller
                      control={control}
                      name="neighborhood"
                      as={
                        <Input
                          placeholder="Mahalle yazınız"
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                        />
                      }
                    />
                  </FormItem>
                </Col>
                <Col xs={24}>
                  <FormItem
                    label="Adres"
                    error={errors.address}
                    required={true}
                  >
                    <Controller
                      control={control}
                      name="address"
                      as={
                        <Input.TextArea
                          rows={3}
                          placeholder="Adres yazınız"
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                        />
                      }
                    />
                  </FormItem>
                </Col>
              </Row>
            </FormSection>
            <FormSection title="Ölçüler">
              <MeasureList form={form} mode={mode} />
            </FormSection>

            {mode === DISPLAY_MODE.READ_ONLY ? null : (
              <Row
                justify="space-between"
                style={{
                  borderTop: "1px dashed rgb(166, 157, 152)",
                  position: "sticky",
                  bottom: 0,
                  padding: "16px 0",
                  backgroundColor: "#f2f2f2",
                }}
              >
                <Col>
                  <Button
                    loading={
                      store.process === ACTION_TYPES.CREATING_DATA ||
                      store.process === ACTION_TYPES.UPDATING_DATA
                    }
                    style={{ width: 120, marginRight: 8 }}
                    type="primary"
                    htmlType="submit"
                  >
                    Kaydet
                  </Button>

                  <Button
                    onClick={() => {
                      Modal.confirm({
                        title: "İptal",
                        content:
                          "Kaydetmediğiniz tüm değişiklikler, geri alınacaktır.",
                        okText: "Evet",
                        cancelText: "İptal",
                        onOk: () => {
                          reset();
                        },
                      });
                    }}
                  >
                    İptal
                  </Button>
                </Col>
              </Row>
            )}
          </Col>
        </FormLayout>
      </form>
    </Spin>
  );
}
